<template>
  <!-- «Definición»: nombre y descripción (se editan en el dto, sin historial), la entidad con «Cambiar…» y Borrar. -->
  <div ref="rootRef" class="dp" @keydown.esc="onEsc">
    <button ref="btnRef" type="button" class="btn" aria-haspopup="dialog" :aria-expanded="isOpen"
            @click="isOpen ? close(false) : open('name')">{{ t('ins.definition') }}</button>
    <Transition name="vem-pop">
      <div v-if="isOpen" class="dp-pop" role="dialog" :aria-label="t('ins.definition')">
        <label class="dp-row">
          <span class="dp-lbl">{{ t('ed.name') }}</span>
          <input ref="nameRef" v-model="dto.name" class="in" />
        </label>
        <label class="dp-row">
          <span class="dp-lbl">{{ t('ins.description') }}</span>
          <textarea v-model="dto.description" class="in" rows="3" />
        </label>
        <div class="dp-row">
          <span class="dp-lbl">{{ t('ed.mapping') }}</span>
          <div class="dp-ent">
            <span class="dp-ent-name" :class="{ none: !dto.rootEntity }">
              <span class="mc-ico entity" aria-hidden="true">E</span>
              <b class="mono">{{ dto.rootEntity ? comboName(dto.rootEntity) : t('ed.noEntity') }}</b>
            </span>
            <span v-if="dto.rootEntity" class="mono sm muted dp-tables">{{ comboTables(dto.rootEntity) }}</span>
            <button ref="changeRef" type="button" class="btn dp-change" @click="changeEntity">{{ t('def.change') }}</button>
          </div>
        </div>
        <template v-if="canDelete">
          <hr class="dp-sep" />
          <button type="button" class="btn danger" @click="doDelete">{{ t('ed.delete') }}</button>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { t } from '@vem/i18n.js';
import { comboName, comboTables } from '@vem/catalog.js';

defineProps({
  /** dto del editor: se editan name y description. */
  dto: { type: Object, required: true },
  canDelete: { type: Boolean, default: false },
});
const emit = defineEmits(['change-entity', 'delete']);

const isOpen = ref(false);
const rootRef = ref(null);
const btnRef = ref(null);
const nameRef = ref(null);
const changeRef = ref(null);

/** Abre el popover con el foco en el nombre ('name') o en «Cambiar…» ('entity'). */
function open(focus) {
  isOpen.value = true;
  nextTick(() => (focus === 'entity' ? changeRef.value : nameRef.value)?.focus());
}

function close(refocus) {
  isOpen.value = false;
  if (refocus) nextTick(() => btnRef.value?.focus());
}

function changeEntity() {
  close(false);
  emit('change-entity');
}

function doDelete() {
  close(false);
  emit('delete');
}

function onEsc(e) {
  if (!isOpen.value) return;
  e.stopPropagation();
  close(true);
}

function onDocDown(e) {
  if (isOpen.value && rootRef.value && !rootRef.value.contains(e.target)) close(false);
}

onMounted(() => document.addEventListener('pointerdown', onDocDown));
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocDown));

defineExpose({ open });
</script>

<style>
.vem-root .dp { position: relative; }
.vem-root .dp-pop {
  position: absolute; z-index: 20; top: calc(100% + 8px); right: 0; width: 400px; max-width: calc(100vw - 32px);
  background: var(--panel); border: 1px solid var(--line2); border-radius: 12px; padding: 12px; box-shadow: 0 14px 36px var(--shadow);
  display: flex; flex-direction: column; gap: 10px;
}
.vem-root .dp-row { display: grid; grid-template-columns: 92px minmax(0, 1fr); gap: 10px; align-items: start; }
.vem-root .dp-lbl { font-size: 12px; color: var(--tx2); padding-top: 8px; }
.vem-root .dp-row .in { width: 100%; }
.vem-root .dp-ent { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding-top: 4px; min-width: 0; }
.vem-root .dp-ent-name { display: inline-flex; align-items: center; gap: 7px; }
.vem-root .dp-ent-name .mc-ico { flex: 0 0 20px; height: 20px; font-size: 9px; border-radius: 5px; }
.vem-root .dp-ent-name b { font-size: 13px; color: var(--lime); overflow-wrap: anywhere; }
.vem-root .dp-ent-name.none b { color: var(--amber); }
.vem-root .dp-tables { padding-left: 27px; }
.vem-root .dp-change { align-self: flex-end; min-height: 32px; padding: 5px 12px; margin-top: 4px; }
.vem-root .dp-sep { width: 100%; border: 0; border-top: 1px solid var(--line2); margin: 2px 0; }
.vem-root .dp-pop > .btn.danger { align-self: flex-start; }
</style>
